import {
  collection,
  addDoc,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  runTransaction,
  serverTimestamp,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import { Match, QueueEntry, Question } from '../types';

/**
 * Firestore Collections:
 * - matchmakingQueue: Users waiting for a match
 * - matches: Active and historical matches
 */

// ============================================================================
// MATCHMAKING QUEUE
// ============================================================================

/**
 * Add a user to the matchmaking queue
 * Uses transaction to ensure atomicity
 */
export const addToQueue = async (
  uid: string,
  displayName: string,
  avatar: number,
  rank: string
): Promise<string> => {
  try {
    console.log('🎯 Adding user to queue:', { uid, displayName, rank });

    // First check if user is already in queue
    const queueRef = collection(db, 'matchmakingQueue');
    const existingQuery = query(queueRef, where('uid', '==', uid));
    const existingSnapshot = await getDocs(existingQuery);

    if (!existingSnapshot.empty) {
      console.log('⚠️ User already in queue');
      return existingSnapshot.docs[0].id;
    }

    // Add to queue
    const queueEntry: Omit<QueueEntry, 'timestamp'> & { timestamp: any } = {
      uid,
      displayName,
      avatar,
      rank: rank as any,
      timestamp: serverTimestamp(),
    };

    const docRef = await addDoc(queueRef, queueEntry);
    console.log('✅ Added to queue with ID:', docRef.id);

    return docRef.id;
  } catch (error) {
    console.error('❌ Error adding to queue:', error);
    throw error;
  }
};

/**
 * Remove a user from the matchmaking queue
 */
export const removeFromQueue = async (uid: string): Promise<void> => {
  try {
    console.log('🚫 Removing user from queue:', uid);

    const queueRef = collection(db, 'matchmakingQueue');
    const q = query(queueRef, where('uid', '==', uid));
    const snapshot = await getDocs(q);

    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log('✅ Removed from queue');
  } catch (error) {
    console.error('❌ Error removing from queue:', error);
    throw error;
  }
};

/**
 * Find an opponent from the matchmaking queue
 * Returns the first available player (FIFO)
 */
export const findOpponent = async (
  currentUserId: string
): Promise<QueueEntry | null> => {
  try {
    console.log('🔍 Looking for opponent...');

    const queueRef = collection(db, 'matchmakingQueue');
    const q = query(
      queueRef,
      orderBy('timestamp', 'asc'),
      limit(10) // Get up to 10 entries and filter client-side
    );

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('⏳ No opponents available');
      return null;
    }

    // Filter out current user, users already in a match, and stale entries
    const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000);

    const opponents = snapshot.docs
      .map((doc) => doc.data())
      .filter((data) => {
        // Skip current user
        if (data.uid === currentUserId) return false;

        // Skip users already matched
        if (data.matchId) return false;

        // Skip stale entries (older than 2 minutes)
        const entryTime = data.timestamp?.toDate() || new Date(0);
        if (entryTime < twoMinutesAgo) {
          console.log('⏭️ Skipping stale queue entry:', data.displayName);
          return false;
        }

        return true;
      });

    if (opponents.length === 0) {
      console.log('⏳ No opponents available (only found self, matched, or stale players)');
      return null;
    }

    const opponentData = opponents[0];
    console.log('✅ Found opponent:', opponentData.displayName);

    return {
      uid: opponentData.uid,
      displayName: opponentData.displayName,
      avatar: opponentData.avatar,
      rank: opponentData.rank,
      timestamp: opponentData.timestamp?.toDate() || new Date(),
    };
  } catch (error) {
    console.error('❌ Error finding opponent:', error);
    throw error;
  }
};

// ============================================================================
// MATCH MANAGEMENT
// ============================================================================

/**
 * Create a new match between two players
 * Generates questions and initializes match state
 */
export const createMatch = async (
  player1: {
    uid: string;
    displayName: string;
    avatar: number;
  },
  player2: {
    uid: string;
    displayName: string;
    avatar: number;
  },
  questions: Question[]
): Promise<string> => {
  try {
    console.log('🎮 Creating match between:', player1.displayName, 'vs', player2.displayName);

    const matchData: Omit<Match, 'id' | 'createdAt' | 'startedAt' | 'completedAt'> & {
      createdAt: any;
    } = {
      player1: {
        uid: player1.uid,
        displayName: player1.displayName,
        avatar: player1.avatar,
        score: 0,
        answers: [],
        connected: true,
        ready: false,
      },
      player2: {
        uid: player2.uid,
        displayName: player2.displayName,
        avatar: player2.avatar,
        score: 0,
        answers: [],
        connected: true,
        ready: false,
      },
      questions,
      currentQuestionIndex: 0,
      status: 'waiting',
      winnerId: null,
      createdAt: serverTimestamp(),
    };

    const matchRef = await addDoc(collection(db, 'matches'), matchData);
    console.log('✅ Match created with ID:', matchRef.id);

    // Update player1's own queue entry with matchId so player2 can find it
    // Player1 updates their own entry (allowed by security rules)
    const queueRef = collection(db, 'matchmakingQueue');
    const player1Query = query(queueRef, where('uid', '==', player1.uid));
    const player1Snapshot = await getDocs(player1Query);

    if (!player1Snapshot.empty) {
      await updateDoc(player1Snapshot.docs[0].ref, {
        matchId: matchRef.id,
      });
      console.log('✅ Updated player1 queue entry with matchId');
    }

    // Don't remove from queue yet - both players will remove themselves after joining

    return matchRef.id;
  } catch (error) {
    console.error('❌ Error creating match:', error);
    throw error;
  }
};

/**
 * Mark player as ready
 */
export const setPlayerReady = async (
  matchId: string,
  playerId: string
): Promise<void> => {
  try {
    console.log('✅ Setting player ready:', playerId);

    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();
    const isPlayer1 = matchData.player1.uid === playerId;

    console.log('📝 Updating Firestore - setting', isPlayer1 ? 'player1' : 'player2', 'ready to true');
    await updateDoc(matchRef, {
      [`${isPlayer1 ? 'player1' : 'player2'}.ready`]: true,
    });
    console.log('✅ Firestore update complete!');

    // If both players are ready, start the match
    const player1Ready = isPlayer1 ? true : matchData.player1.ready;
    const player2Ready = !isPlayer1 ? true : matchData.player2.ready;

    if (player1Ready && player2Ready) {
      await updateDoc(matchRef, {
        status: 'in_progress',
        startedAt: serverTimestamp(),
      });
      console.log('🎮 Match started!');

      // Clean up queue entries for both players (silently ignore errors)
      await Promise.all([
        removeFromQueue(matchData.player1.uid).catch((err) =>
          console.log('⚠️ Could not remove player1 from queue (may already be removed):', err.message)
        ),
        removeFromQueue(matchData.player2.uid).catch((err) =>
          console.log('⚠️ Could not remove player2 from queue (may already be removed):', err.message)
        ),
      ]);
      console.log('✅ Cleaned up queue entries');
    }
  } catch (error) {
    console.error('❌ Error setting player ready:', error);
    throw error;
  }
};

/**
 * Submit an answer for a player in a match
 */
export const submitAnswer = async (
  matchId: string,
  playerId: string,
  answer: {
    questionId: string;
    selectedAnswer: number;
    isCorrect: boolean;
    timeSpent: number;
    points: number;
  }
): Promise<void> => {
  try {
    console.log('📝 Submitting answer for player:', playerId);

    await runTransaction(db, async (transaction) => {
      const matchRef = doc(db, 'matches', matchId);
      const matchDoc = await transaction.get(matchRef);

      if (!matchDoc.exists()) {
        throw new Error('Match not found');
      }

      const matchData = matchDoc.data();
      const isPlayer1 = matchData.player1.uid === playerId;
      const playerKey = isPlayer1 ? 'player1' : 'player2';

      // Add answer to player's answers array
      const currentAnswers = matchData[playerKey].answers || [];
      const newAnswers = [...currentAnswers, answer];

      // Update score
      const newScore = matchData[playerKey].score + answer.points;

      transaction.update(matchRef, {
        [`${playerKey}.answers`]: newAnswers,
        [`${playerKey}.score`]: newScore,
      });
    });

    console.log('✅ Answer submitted');

    // After transaction completes, re-read match data to check if both answered
    // This avoids race condition when both players submit simultaneously
    setTimeout(async () => {
      try {
        const matchRef = doc(db, 'matches', matchId);
        const matchDoc = await getDoc(matchRef);

        if (!matchDoc.exists()) {
          console.log('⚠️ Match not found when checking advance');
          return;
        }

        const matchData = matchDoc.data();
        const currentQuestionIndex = matchData.currentQuestionIndex;
        const player1Answers = matchData.player1.answers || [];
        const player2Answers = matchData.player2?.answers || [];

        console.log(`📊 Post-submit check - Q${currentQuestionIndex}: P1 answers=${player1Answers.length}, P2 answers=${player2Answers.length}`);

        // Check if both players have answered current question
        if (player1Answers.length > currentQuestionIndex && player2Answers.length > currentQuestionIndex) {
          console.log('✅ Both players answered! Auto-advancing to next question in 2.5s...');
          // Wait 2.5 seconds to let players see both answers and results
          await new Promise(resolve => setTimeout(resolve, 2500));
          await advanceQuestion(matchId);
        } else {
          console.log('⏳ Waiting for opponent to answer...');
        }
      } catch (err) {
        console.error('❌ Error checking for auto-advance:', err);
      }
    }, 500); // Small delay to ensure transaction fully commits
  } catch (error) {
    console.error('❌ Error submitting answer:', error);
    throw error;
  }
};

/**
 * Advance to the next question
 */
export const advanceQuestion = async (matchId: string): Promise<void> => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();

    // Don't advance if match is already completed
    if (matchData.status === 'completed') {
      console.log('⏭️ Match already completed, skipping advance');
      return;
    }

    const currentIndex = matchData.currentQuestionIndex;
    const nextIndex = currentIndex + 1;
    const totalQuestions = matchData.questions.length;

    console.log(`⏭️ Advancing question: ${currentIndex} → ${nextIndex} (total: ${totalQuestions})`);

    // Check if match is complete
    if (nextIndex >= totalQuestions) {
      console.log('🏁 Last question reached, completing match...');
      await completeMatch(matchId);
    } else {
      console.log(`📝 Moving to question ${nextIndex}`);
      await updateDoc(matchRef, {
        currentQuestionIndex: nextIndex,
      });
    }
  } catch (error) {
    console.error('❌ Error advancing question:', error);
    throw error;
  }
};

/**
 * Complete a match and determine the winner
 * This only updates the match document - players update their own stats separately
 */
export const completeMatch = async (matchId: string): Promise<void> => {
  try {
    console.log('🏁 Completing match:', matchId);

    await runTransaction(db, async (transaction) => {
      const matchRef = doc(db, 'matches', matchId);
      const matchDoc = await transaction.get(matchRef);

      if (!matchDoc.exists()) {
        throw new Error('Match not found');
      }

      const matchData = matchDoc.data();

      // Don't complete if already completed (prevents duplicate calls)
      if (matchData.status === 'completed') {
        console.log('⏭️ Match already completed, skipping');
        return;
      }

      const player1 = matchData.player1;
      const player2 = matchData.player2;
      const player1Score = player1.score;
      const player2Score = player2.score;

      // Determine winner
      let winnerId: string | null = null;
      if (player1Score > player2Score) {
        winnerId = player1.uid;
      } else if (player2Score > player1Score) {
        winnerId = player2.uid;
      }
      // null means draw

      console.log('✅ Match completed. Winner:', winnerId || 'Draw');

      // Only update the match document - players will update their own stats
      transaction.update(matchRef, {
        status: 'completed',
        winnerId,
        completedAt: serverTimestamp(),
      });
    });
  } catch (error) {
    console.error('❌ Error completing match:', error);
    throw error;
  }
};

/**
 * Update player's own stats after match completion
 * Called by each player separately to update their own stats
 */
export const updateOwnStatsAfterMatch = async (
  matchId: string,
  playerId: string
): Promise<void> => {
  try {
    console.log('📊 Updating own stats for player:', playerId);

    // Get match data to calculate stats
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();
    const isPlayer1 = matchData.player1.uid === playerId;
    const player = isPlayer1 ? matchData.player1 : matchData.player2;
    const playerScore = player.score;
    const winnerId = matchData.winnerId;

    const isWinner = winnerId === playerId;
    const isDraw = winnerId === null;

    // Update own user document
    await runTransaction(db, async (transaction) => {
      const userRef = doc(db, 'users', playerId);
      const userDoc = await transaction.get(userRef);

      if (!userDoc.exists()) {
        console.log('⚠️ User not found:', playerId);
        return;
      }

      const userData = userDoc.data();
      const mpStats = userData.multiplayerStats || {
        gamesPlayed: 0,
        wins: 0,
        losses: 0,
        draws: 0,
        totalScore: 0,
        averageScore: 0,
        bestScore: 0,
        winRate: 0,
        currentStreak: 0,
        longestStreak: 0,
      };

      // Update stats
      const newGamesPlayed = mpStats.gamesPlayed + 1;
      const newWins = isWinner ? mpStats.wins + 1 : mpStats.wins;
      const newLosses = (!isWinner && !isDraw) ? mpStats.losses + 1 : mpStats.losses;
      const newDraws = isDraw ? mpStats.draws + 1 : mpStats.draws;
      const newTotalScore = mpStats.totalScore + playerScore;
      const newAverageScore = Math.round(newTotalScore / newGamesPlayed);
      const newBestScore = Math.max(mpStats.bestScore, playerScore);
      const newWinRate = Math.round((newWins / newGamesPlayed) * 100);

      // Update streak
      let newCurrentStreak = mpStats.currentStreak;
      let newLongestStreak = mpStats.longestStreak;
      if (isWinner) {
        newCurrentStreak += 1;
        newLongestStreak = Math.max(newLongestStreak, newCurrentStreak);
      } else if (!isDraw) {
        newCurrentStreak = 0;
      }

      // Calculate XP reward
      let xpReward = 0;
      if (isWinner) {
        xpReward = 100 + Math.floor(playerScore / 10);
      } else if (isDraw) {
        xpReward = 50 + Math.floor(playerScore / 20);
      } else {
        xpReward = 25 + Math.floor(playerScore / 20);
      }

      const newXP = (userData.xp || 0) + xpReward;

      console.log(`✅ Stats updated: ${isWinner ? 'WIN' : isDraw ? 'DRAW' : 'LOSS'} - Score: ${playerScore}, XP: +${xpReward}`);

      transaction.update(userRef, {
        xp: newXP,
        'multiplayerStats.gamesPlayed': newGamesPlayed,
        'multiplayerStats.wins': newWins,
        'multiplayerStats.losses': newLosses,
        'multiplayerStats.draws': newDraws,
        'multiplayerStats.totalScore': newTotalScore,
        'multiplayerStats.averageScore': newAverageScore,
        'multiplayerStats.bestScore': newBestScore,
        'multiplayerStats.winRate': newWinRate,
        'multiplayerStats.currentStreak': newCurrentStreak,
        'multiplayerStats.longestStreak': newLongestStreak,
      });
    });
  } catch (error) {
    console.error('❌ Error updating own stats:', error);
    throw error;
  }
};

// ============================================================================
// REAL-TIME LISTENERS
// ============================================================================

/**
 * Listen to match updates in real-time
 */
export const listenToMatch = (
  matchId: string,
  callback: (match: Match) => void
): Unsubscribe => {
  const matchRef = doc(db, 'matches', matchId);

  return onSnapshot(
    matchRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const match: Match = {
          id: snapshot.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          startedAt: data.startedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
        } as Match;

        callback(match);
      }
    },
    (error) => {
      console.error('❌ Error listening to match:', error);
    }
  );
};

/**
 * Listen to matchmaking queue for opponent found
 */
export const listenToQueueForMatch = (
  userId: string,
  onMatchFound: (matchId: string) => void
): Unsubscribe => {
  const matchesRef = collection(db, 'matches');
  const q = query(
    matchesRef,
    where('status', '==', 'waiting'),
    orderBy('createdAt', 'desc'),
    limit(10)
  );

  return onSnapshot(q, (snapshot) => {
    snapshot.docs.forEach((doc) => {
      const match = doc.data();
      const isPlayer1 = match.player1.uid === userId;
      const isPlayer2 = match.player2?.uid === userId;

      if (isPlayer1 || isPlayer2) {
        console.log('🎉 Match found!');
        onMatchFound(doc.id);
      }
    });
  });
};

// ============================================================================
// CONNECTION HANDLING
// ============================================================================

/**
 * Update player connection status
 */
export const updateConnectionStatus = async (
  matchId: string,
  playerId: string,
  connected: boolean
): Promise<void> => {
  try {
    console.log(`${connected ? '🟢' : '🔴'} Player connection status:`, playerId, connected);

    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();
    const isPlayer1 = matchData.player1.uid === playerId;

    await updateDoc(matchRef, {
      [`${isPlayer1 ? 'player1' : 'player2'}.connected`]: connected,
    });
  } catch (error) {
    console.error('❌ Error updating connection status:', error);
    throw error;
  }
};

/**
 * Forfeit a match (disconnect timeout or manual forfeit)
 * Only updates the match document - players update their own stats separately
 */
export const forfeitMatch = async (
  matchId: string,
  playerId: string
): Promise<void> => {
  try {
    console.log('🏳️ Player forfeiting match:', playerId);

    await runTransaction(db, async (transaction) => {
      const matchRef = doc(db, 'matches', matchId);
      const matchDoc = await transaction.get(matchRef);

      if (!matchDoc.exists()) {
        throw new Error('Match not found');
      }

      const matchData = matchDoc.data();

      // Don't forfeit if already completed
      if (matchData.status === 'completed') {
        console.log('⏭️ Match already completed, skipping forfeit');
        return;
      }

      const isPlayer1 = matchData.player1.uid === playerId;
      const winnerId = isPlayer1 ? matchData.player2.uid : matchData.player1.uid;

      // Only update the match document - players will update their own stats
      transaction.update(matchRef, {
        status: 'completed',
        winnerId,
        completedAt: serverTimestamp(),
      });
    });

    console.log('✅ Match forfeited');
  } catch (error) {
    console.error('❌ Error forfeiting match:', error);
    throw error;
  }
};

/**
 * Get match by ID
 */
export const getMatch = async (matchId: string): Promise<Match | null> => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      return null;
    }

    const data = matchDoc.data();
    return {
      id: matchDoc.id,
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
      startedAt: data.startedAt?.toDate(),
      completedAt: data.completedAt?.toDate(),
    } as Match;
  } catch (error) {
    console.error('❌ Error getting match:', error);
    return null;
  }
};

/**
 * Check if opponent's queue entry has a matchId assigned
 * Player 2 uses this to find the match created by Player 1
 */
export const checkOpponentQueueForMatch = async (opponentUid: string): Promise<string | null> => {
  try {
    const queueRef = collection(db, 'matchmakingQueue');
    const q = query(queueRef, where('uid', '==', opponentUid));
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.log('⏳ Opponent not in queue');
      return null;
    }

    const queueData = snapshot.docs[0].data();
    if (queueData.matchId) {
      console.log('✅ Found matchId in opponent queue:', queueData.matchId);
    }
    return queueData.matchId || null;
  } catch (error) {
    console.error('❌ Error checking opponent queue for match:', error);
    return null;
  }
};

/**
 * Get match history for a user
 */
export const getMatchHistory = async (
  userId: string,
  limitCount: number = 20
): Promise<Match[]> => {
  try {
    const matchesRef = collection(db, 'matches');

    // Query matches where user is either player1 or player2
    const q1 = query(
      matchesRef,
      where('player1.uid', '==', userId),
      where('status', '==', 'completed'),
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );

    const q2 = query(
      matchesRef,
      where('player2.uid', '==', userId),
      where('status', '==', 'completed'),
      orderBy('completedAt', 'desc'),
      limit(limitCount)
    );

    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2),
    ]);

    const matches: Match[] = [];

    [...snapshot1.docs, ...snapshot2.docs].forEach((doc) => {
      const data = doc.data();
      matches.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        startedAt: data.startedAt?.toDate(),
        completedAt: data.completedAt?.toDate(),
      } as Match);
    });

    // Sort by completedAt descending and limit
    matches.sort((a, b) => {
      const aTime = a.completedAt?.getTime() || 0;
      const bTime = b.completedAt?.getTime() || 0;
      return bTime - aTime;
    });

    return matches.slice(0, limitCount);
  } catch (error) {
    console.error('❌ Error getting match history:', error);
    return [];
  }
};

// ============================================================================
// REMATCH SYSTEM
// ============================================================================

/**
 * Request a rematch with the opponent from a completed match
 */
export const requestRematch = async (
  matchId: string,
  playerId: string
): Promise<void> => {
  try {
    console.log('🔄 Requesting rematch for match:', matchId);

    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();

    if (matchData.status !== 'completed') {
      throw new Error('Can only request rematch for completed matches');
    }

    // Update match with rematch request
    await updateDoc(matchRef, {
      rematchRequestedBy: playerId,
      rematchAccepted: false,
    });

    console.log('✅ Rematch requested');
  } catch (error) {
    console.error('❌ Error requesting rematch:', error);
    throw error;
  }
};

/**
 * Accept a rematch request and create a new match
 */
export const acceptRematch = async (
  matchId: string,
  playerId: string,
  questions: Question[]
): Promise<string> => {
  try {
    console.log('✅ Accepting rematch for match:', matchId);

    const matchRef = doc(db, 'matches', matchId);
    const matchDoc = await getDoc(matchRef);

    if (!matchDoc.exists()) {
      throw new Error('Match not found');
    }

    const matchData = matchDoc.data();

    if (!matchData.rematchRequestedBy || matchData.rematchRequestedBy === playerId) {
      throw new Error('No rematch request to accept');
    }

    // Create new match with same players
    const player1 = matchData.player1;
    const player2 = matchData.player2;

    const newMatchId = await createMatch(
      { uid: player1.uid, displayName: player1.displayName, avatar: player1.avatar },
      { uid: player2.uid, displayName: player2.displayName, avatar: player2.avatar },
      questions
    );

    // Update original match with rematch info
    await updateDoc(matchRef, {
      rematchAccepted: true,
      rematchMatchId: newMatchId,
    });

    console.log('✅ Rematch accepted, new match created:', newMatchId);
    return newMatchId;
  } catch (error) {
    console.error('❌ Error accepting rematch:', error);
    throw error;
  }
};

/**
 * Decline a rematch request
 */
export const declineRematch = async (
  matchId: string
): Promise<void> => {
  try {
    console.log('❌ Declining rematch for match:', matchId);

    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, {
      rematchRequestedBy: null,
      rematchAccepted: false,
    });

    console.log('✅ Rematch declined');
  } catch (error) {
    console.error('❌ Error declining rematch:', error);
    throw error;
  }
};

/**
 * Cancel a rematch request (by the requester)
 */
export const cancelRematchRequest = async (
  matchId: string
): Promise<void> => {
  try {
    console.log('🚫 Canceling rematch request for match:', matchId);

    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, {
      rematchRequestedBy: null,
      rematchAccepted: false,
    });

    console.log('✅ Rematch request canceled');
  } catch (error) {
    console.error('❌ Error canceling rematch request:', error);
    throw error;
  }
};

// ============================================================================
// RIVALRY STATS
// ============================================================================

/**
 * Get rivalry stats between two players
 */
export const getRivalryStats = async (
  player1Id: string,
  player2Id: string
): Promise<{
  totalGames: number;
  player1Wins: number;
  player2Wins: number;
  draws: number;
  lastMatch?: Match;
}> => {
  try {
    console.log('📊 Getting rivalry stats between:', player1Id, 'and', player2Id);

    const matchesRef = collection(db, 'matches');

    // Get all completed matches between these two players
    const q = query(
      matchesRef,
      where('status', '==', 'completed')
    );

    const snapshot = await getDocs(q);

    const rivalryMatches: Match[] = [];

    snapshot.docs.forEach((doc) => {
      const data = doc.data();
      const isRivalryMatch =
        (data.player1.uid === player1Id && data.player2?.uid === player2Id) ||
        (data.player1.uid === player2Id && data.player2?.uid === player1Id);

      if (isRivalryMatch) {
        rivalryMatches.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          startedAt: data.startedAt?.toDate(),
          completedAt: data.completedAt?.toDate(),
        } as Match);
      }
    });

    // Calculate stats
    let player1Wins = 0;
    let player2Wins = 0;
    let draws = 0;

    rivalryMatches.forEach((match) => {
      if (match.winnerId === null) {
        draws++;
      } else if (match.winnerId === player1Id) {
        player1Wins++;
      } else if (match.winnerId === player2Id) {
        player2Wins++;
      }
    });

    // Sort by completedAt to get last match
    rivalryMatches.sort((a, b) => {
      const aTime = a.completedAt?.getTime() || 0;
      const bTime = b.completedAt?.getTime() || 0;
      return bTime - aTime;
    });

    const lastMatch = rivalryMatches[0];

    console.log(`✅ Rivalry stats: ${player1Wins}-${player2Wins}-${draws} (${rivalryMatches.length} total games)`);

    return {
      totalGames: rivalryMatches.length,
      player1Wins,
      player2Wins,
      draws,
      lastMatch,
    };
  } catch (error) {
    console.error('❌ Error getting rivalry stats:', error);
    return {
      totalGames: 0,
      player1Wins: 0,
      player2Wins: 0,
      draws: 0,
    };
  }
};
